import { type UpdateLabelMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateLabelMutationDocument } from "./label.gql";
import { EVENT_LABEL_UPDATED, trackLabelEvent } from "./tracking";

export type UpdatedLabel = UpdateLabelMutation["updateLabel"];

export interface UpdateLabelInput {
  id: string;
  title?: string;
  color?: string;
  labelGroupId?: string | null;
}

export interface UseUpdateLabelProps {
  onCompleted?: (data: UpdatedLabel) => void;
  onError?: (e: Error) => void;
}

export const useUpdateLabel = ({ onCompleted, onError }: UseUpdateLabelProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: UpdateLabelInput) => {
      const client = createOperationClient("UpdateLabel");
      const data = await client.request(updateLabelMutationDocument, { input });

      return data.updateLabel;
    },
    onSuccess: (label) => {
      queryClient.setQueryData(queryKeys.labels.detail(label.id), label);

      queryClient.invalidateQueries({
        queryKey: queryKeys.labels.list(label.workspaceId),
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.labelGroups.details(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.labelGroups.list(label.workspaceId),
      });

      trackLabelEvent(EVENT_LABEL_UPDATED, {
        labelId: label.id,
      });

      if (onCompleted) {
        onCompleted(label);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    updateLabel: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
